import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Trophy, DollarSign, Gem, Star, Plane, Crown,
  Medal, Award, Users, TrendingUp, Shield, Lock
} from "lucide-react";

const ICON_MAP = {
  "trophy": Trophy,
  "dollar-sign": DollarSign,
  "gem": Gem,
  "star": Star,
  "plane": Plane,
  "crown": Crown,
  "medal": Medal,
  "award": Award,
  "users": Users,
  "trending-up": TrendingUp,
  "shield": Shield,
};

export default function Achievements({ team }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (team?.id) fetchAchievements();
  }, [team?.id]);

  const fetchAchievements = async () => {
    try {
      const res = await axios.get(`${API}/teams/${team.id}/achievements`);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load achievements");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="achievements-loading">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-36 bg-slate-200 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  const unlocked = data?.achievements?.filter((a) => a.unlocked) || [];
  const locked = data?.achievements?.filter((a) => !a.unlocked) || [];
  const progress = data?.total_achievements
    ? Math.round((data.unlocked_count / data.total_achievements) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="achievements-tab">
      {/* Summary card */}
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center">
                <Trophy className="w-7 h-7 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">{t("achievements.achievementPoints")}</p>
                <p className="text-3xl font-heading font-bold text-slate-900" data-testid="total-points">
                  {data?.total_points || 0}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">{t("achievements.progress")}</p>
              <p className="text-lg font-bold text-slate-900" data-testid="achievement-progress">
                {data?.unlocked_count || 0} / {data?.total_achievements || 0}
              </p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-700"
              style={{ width: `${progress}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Unlocked */}
      {unlocked.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
            {t("achievements.unlocked")} ({unlocked.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {unlocked.map((ach) => (
              <AchievementCard key={ach.id} achievement={ach} unlocked />
            ))}
          </div>
        </div>
      )}

      {/* Locked */}
      {locked.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
            {t("achievements.locked")} ({locked.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {locked.map((ach) => (
              <AchievementCard key={ach.id} achievement={ach} unlocked={false} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AchievementCard({ achievement, unlocked }) {
  const Icon = ICON_MAP[achievement.icon] || Trophy;

  return (
    <Card
      className={`relative overflow-hidden transition-all duration-300 ${
        unlocked
          ? "border-amber-200 bg-gradient-to-br from-amber-50 to-white hover:shadow-md hover:shadow-amber-100"
          : "border-slate-200 bg-slate-50 opacity-60"
      }`}
      data-testid={`achievement-${achievement.id}`}
    >
      <CardContent className="p-4 text-center">
        <div
          className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${
            unlocked ? "bg-amber-100" : "bg-slate-200"
          }`}
        >
          {unlocked ? (
            <Icon className="w-6 h-6 text-amber-600" />
          ) : (
            <Lock className="w-5 h-5 text-slate-400" />
          )}
        </div>
        <p className={`font-heading font-semibold text-sm ${unlocked ? "text-slate-900" : "text-slate-500"}`}>
          {achievement.name}
        </p>
        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{achievement.description}</p>
        <div className="mt-2 flex items-center justify-center gap-1">
          <Star className="w-3 h-3 text-amber-500" />
          <span className="text-xs font-mono font-medium text-slate-600">{achievement.points} pts</span>
        </div>
        {unlocked && achievement.unlocked_at && (
          <p className="text-[10px] text-slate-400 mt-1">
            {new Date(achievement.unlocked_at).toLocaleDateString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
